import { useCallback, useState } from 'react';
import Seo from '../components/Seo.jsx';
import Eyebrow from '../components/ui/Eyebrow.jsx';
import CheckIcon from '../components/ui/CheckIcon.jsx';
import DemoRequestForm from '../components/DemoRequestForm.jsx';

const WHAT_HAPPENS = [
  'A short call of 20 minutes, on WhatsApp or a phone call.',
  'We walk through your own numbers, not a generic slide deck.',
  'If it fits, we set up your farm with your existing records loaded.',
  'You see a working dashboard before you commit to anything.',
];

export default function Demo() {
  const [sent, setSent] = useState(false);

  // Stable identity so the form's effect does not re-run on every render.
  const handleSuccess = useCallback(() => setSent(true), []);

  return (
    <>
      <Seo
        title="Book a demo"
        description="Book a free 20-minute Chickrah demo on WhatsApp or phone. See it working with your own flocks, eggs and feed. No card, no commitment."
        path="/demo"
      />

      <section className="bg-deep pt-14 pb-12">
        <div className="wrap">
          <Eyebrow className="mb-3 !text-amber">Free 20-minute demo</Eyebrow>
          <h1 className="mb-4 max-w-[640px] font-display text-[28px] font-bold leading-tight text-white lg:text-[38px]">
            See how Chickrah can run your farm.
          </h1>
          <p className="max-w-[560px] text-[15.5px] text-mist">
            Book a short call on WhatsApp or phone. We&rsquo;ll show you Chickrah working with your
            own flocks, eggs and feed, and answer every question. No card, no commitment.
          </p>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        {/*
          The form keeps the same parent element in both states. Swapping
          between two different wrappers would remount it, throwing away the
          submitted state the moment it was set and looping straight back to
          an empty form.
        */}
        <div className={`wrap grid gap-10 ${sent ? '' : 'lg:grid-cols-[1fr_0.75fr] lg:gap-14'}`}>
          <div className={sent ? 'mx-auto w-full max-w-[680px]' : ''}>
            <DemoRequestForm onSuccess={handleSuccess} />
          </div>

          {/* Supporting copy answers objections about booking a call. Once the
              call is booked it is answering a question nobody is asking, so it
              goes and the confirmation gets the full width. */}
          {!sent && (
          <aside>
            <h2 className="mb-4 font-display text-[18px] font-bold text-deep">What happens next</h2>
            <ol className="mb-8 space-y-3">
              {WHAT_HAPPENS.map((step, i) => (
                <li key={step} className="flex gap-3 text-[14px] text-ink-soft">
                  <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-wash font-mono text-[11px] font-semibold text-field">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>

            <div className="rounded-2xl bg-wash p-5">
              <h3 className="mb-3 font-display text-[15px] font-bold text-deep">
                No obligation, either way
              </h3>
              <ul className="space-y-2">
                {[
                  'No card details required',
                  'No contract to sign for the call',
                  'We will tell you if it is not a fit',
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2 text-[13.5px] text-ink-soft">
                    <CheckIcon />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
          )}
        </div>
      </section>
    </>
  );
}
